import { apiClient } from "@base/api/client";
import { type QueryClient, infiniteQueryOptions } from "@tanstack/react-query";
import { type DailyLogSearchResponse, dailyLogSearchResponseSchema } from "./dailyLogSearchSchema";
import { type DailyLogSearchApiParams, type DailyLogSearchConditions, toApiParams } from "./dailyLogSearchState";

export const DAILY_LOG_SEARCH_PAGE_SIZE = 20;

/** 상세 왕복 후 복원을 위해 기본(5분)보다 길게 보관한다 */
const DAILY_LOG_SEARCH_GC_TIME_MS = 1000 * 60 * 30;
const DAILY_LOG_SEARCH_STALE_TIME_MS = 1000 * 60 * 5;

export async function fetchDailyLogSearch(
  projectId: number,
  params: DailyLogSearchApiParams,
  signal?: AbortSignal,
): Promise<DailyLogSearchResponse> {
  const response = await apiClient.get<unknown>(`/project-management/api/projects/${projectId}/daily-logs/search`, {
    params,
    signal,
  });
  return dailyLogSearchResponseSchema.parse(response.data);
}

/** 사용자·프로젝트 단위 prefix. 권한 회수·저장 후 invalidation에 사용한다 */
export function dailyLogSearchProjectKey(userId: number, projectId: number) {
  return ["daily-log-search", userId, projectId] as const;
}

export function dailyLogSearchQueryKey(
  userId: number,
  projectId: number,
  conditions: DailyLogSearchConditions,
  limit: number = DAILY_LOG_SEARCH_PAGE_SIZE,
) {
  const canonicalConditions = {
    keyword: conditions.keyword,
    startDate: conditions.startDate,
    endDate: conditions.endDate,
    contractorIds: conditions.contractorIds,
    tagIds: conditions.tagIds,
    limit,
  };
  return [...dailyLogSearchProjectKey(userId, projectId), canonicalConditions] as const;
}

interface DailyLogSearchQueryParams {
  userId: number;
  projectId: number;
  conditions: DailyLogSearchConditions;
  limit?: number;
}

export function dailyLogSearchQueryOptions({
  userId,
  projectId,
  conditions,
  limit = DAILY_LOG_SEARCH_PAGE_SIZE,
}: DailyLogSearchQueryParams) {
  return infiniteQueryOptions({
    queryKey: dailyLogSearchQueryKey(userId, projectId, conditions, limit),
    queryFn: ({ pageParam, signal }) =>
      fetchDailyLogSearch(projectId, toApiParams(conditions, limit, pageParam), signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    gcTime: DAILY_LOG_SEARCH_GC_TIME_MS,
    staleTime: DAILY_LOG_SEARCH_STALE_TIME_MS,
    // 상세 복귀·명시적 갱신은 별도 경로가 담당한다. 기본 자동 refetch와 겹치지 않게 끈다.
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * 이 프로젝트의 모든 사용자 검색 캐시를 stale로 표시만 한다.
 * 기본 refetch는 하지 않는다. 화면에 있는 목록은 refreshActiveDailyLogSearch로,
 * 화면 밖 목록은 복귀할 때 coordinator가 한 번 갱신한다 (TRD §6.5).
 * projectId를 숫자로 비교해 문자열 route param과 섞여도 누락되지 않게 한다.
 */
export function invalidateDailyLogSearch(queryClient: QueryClient, projectId: number | string) {
  const targetProjectId = Number(projectId);
  return queryClient.invalidateQueries({
    queryKey: ["daily-log-search"],
    predicate: (query) => query.queryKey[2] === targetProjectId,
    refetchType: "none",
  });
}

/**
 * 검색 query key를 로그용 요약으로 바꾼다. 검색 key가 아니면 null.
 * Sentry breadcrumb·error extra에 조건 원문이 남지 않게 한다.
 */
export function summarizeDailyLogSearchQueryKey(queryKey: readonly unknown[]): unknown[] | null {
  const [prefix, userId, projectId, conditions] = queryKey;
  if (prefix !== "daily-log-search") return null;
  if (typeof conditions !== "object" || conditions === null) return [prefix, userId, projectId];

  const { keyword, startDate, endDate, contractorIds, tagIds, limit } = conditions as Partial<
    DailyLogSearchConditions & { limit: number }
  >;
  return [
    prefix,
    userId,
    projectId,
    {
      has_keyword: typeof keyword === "string" && keyword !== "",
      has_date_range: Boolean(startDate || endDate),
      contractor_count: Array.isArray(contractorIds) ? contractorIds.length : 0,
      tag_count: Array.isArray(tagIds) ? tagIds.length : 0,
      limit,
    },
  ];
}
